import { createContext, useContext, useState } from "react";

/** One field's edit that may still be waiting on DynamicComponent's 250ms debounce */
export interface PendingEditHandle {
	/** Sends the waiting edit to valueChanged now */
	flush: () => void;
	/** Drops the waiting edit and shows the field's data value again */
	discard: () => void;
}

/**
 * The fields of one editor. Create it with usePendingEdits() and pass it to WebViewContainer or
 * MobileViewContainer as `pendingEdits` to flush or drop edits that fields have not sent yet
 * (e.g. drop them right before Cancel reverts the item, so they can't land afterwards).
 */
export interface PendingEdits {
	register: (handle: PendingEditHandle) => () => void;
	/** Sends every waiting field edit to valueChanged now (the consumer still applies it asynchronously) */
	flush: () => void;
	/** Drops every waiting field edit and shows each field's data value again */
	discard: () => void;
}

export function createPendingEdits(): PendingEdits {
	const handles = new Set<PendingEditHandle>();
	return {
		register(handle) {
			handles.add(handle);
			return () => {
				handles.delete(handle);
			};
		},
		flush: () => [...handles].forEach((handle) => handle.flush()),
		discard: () => [...handles].forEach((handle) => handle.discard()),
	};
}

/** Used when no registry is passed: fields register into nothing, so nothing changes */
const inertPendingEdits: PendingEdits = {
	register: () => () => undefined,
	flush: () => undefined,
	discard: () => undefined,
};

export const PendingEditsContext = createContext<PendingEdits>(inertPendingEdits);
export const usePendingEditsContext = () => useContext(PendingEditsContext);

/** A registry that lives as long as the calling component, for passing to the editor */
export function usePendingEdits() {
	return useState(createPendingEdits)[0];
}
